import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Save, Eye, EyeOff, Trash2 } from "lucide-react";
import { SERVICES } from "@/data/services";

export default function AdminCaseStudies() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  const load = () => api.get("/admin/case-studies").then((r) => setItems(r.data)).catch(() => toast.error("Erreur"));
  useEffect(() => { load(); }, []);

  const createNew = async () => {
    try {
      const r = await api.post("/admin/case-studies", { title: "Nouvelle étude", summary: "Résumé court de l'étude.", services_used: [], published: false });
      load();
      setActive(r.data);
      toast.success("Étude créée — brouillon");
    } catch { toast.error("Erreur"); }
  };

  return (
    <div data-testid="admin-case-studies">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="display text-2xl sm:text-3xl text-[#0A1128] font-extrabold">Études de cas</h1>
          <p className="text-sm text-[#64748B] mt-1">Publiez vos réalisations sur la page publique <a href="/etudes-de-cas" target="_blank" rel="noreferrer" className="text-[#06B6D4] hover:underline">/etudes-de-cas</a>.</p>
        </div>
        <Button onClick={createNew} data-testid="case-new" className="bg-[#06B6D4] text-[#0A1128] hover:bg-[#67E8F9] font-bold"><Plus size={14} className="mr-1" />Nouvelle étude</Button>
      </div>

      <div className="mt-3 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 bg-white border border-black/8 rounded-2xl p-4 max-h-[75vh] overflow-y-auto">
          {items.length === 0 && <div className="text-sm text-[#64748B]">Aucune étude. Cliquez sur "Nouvelle étude" pour commencer.</div>}
          {items.map((c) => (
            <button key={c.id} onClick={() => setActive(c)} data-testid={`case-item-${c.id}`} className={`w-full text-left p-3 rounded-lg border mb-2 transition-colors ${active?.id === c.id ? "border-[#06B6D4] bg-[#06B6D4]/5" : "border-black/8 hover:bg-[#FAFAFA]"}`}>
              <div className="flex items-center gap-1.5">
                {c.published ? <Eye size={11} className="text-[#A7F3D0]" /> : <EyeOff size={11} className="text-[#FFDCA8]" />}
                <span className="display font-bold text-[#0A1128] truncate">{c.title}</span>
              </div>
              <div className="text-xs text-[#64748B] truncate">{c.client_name || c.sector || "—"}</div>
              <div className="text-[10px] text-[#94a3b8] mt-1">/{c.slug}</div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2">
          {!active && <div className="bg-white border border-black/8 rounded-2xl p-6 text-sm text-[#64748B]">Sélectionnez une étude à gauche.</div>}
          {active && <CaseEditor key={active.id} initial={active} onSaved={(c) => { load(); setActive(c); }} onDeleted={() => { load(); setActive(null); }} />}
        </div>
      </div>
    </div>
  );
}

function CaseEditor({ initial, onSaved, onDeleted }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setForm(initial); }, [initial]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/admin/case-studies/${form.id}`, form);
      toast.success("Enregistré");
      onSaved(r.data);
    } catch { toast.error("Erreur"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer cette étude ?")) return;
    try { await api.delete(`/admin/case-studies/${form.id}`); toast.success("Supprimée"); onDeleted(); }
    catch { toast.error("Erreur"); }
  };

  const toggleService = (sid) => {
    const set = new Set(form.services_used || []);
    if (set.has(sid)) set.delete(sid); else set.add(sid);
    setForm({ ...form, services_used: Array.from(set) });
  };

  return (
    <div className="bg-white border border-black/8 rounded-2xl p-5 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <label className="inline-flex items-center gap-2 text-sm font-bold">
          <input type="checkbox" checked={!!form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
          {form.published ? "Publiée" : "Brouillon"}
        </label>
        <div className="flex gap-2">
          <Button variant="outline" onClick={remove} data-testid="case-delete" className="text-red-600 hover:bg-red-50"><Trash2 size={14} /></Button>
          <Button onClick={save} disabled={busy} data-testid="case-save" className="bg-[#0A1128] text-white"><Save size={14} className="mr-1" />{busy ? "..." : "Enregistrer"}</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2"><Label>Titre *</Label><Input data-testid="case-title" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
        <div><Label>Slug (URL)</Label><Input value={form.slug || ""} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto-généré-depuis-le-titre" /></div>
        <div><Label>Client</Label><Input value={form.client_name || ""} onChange={(e) => setForm({ ...form, client_name: e.target.value })} /></div>
        <div><Label>Secteur</Label><Input value={form.sector || ""} onChange={(e) => setForm({ ...form, sector: e.target.value })} /></div>
        <div><Label>Image de couverture (URL)</Label><Input value={form.cover_image || ""} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} placeholder="https://..." /></div>

        <div className="sm:col-span-2">
          <Label>Services utilisés</Label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {SERVICES.map((s) => {
              const active = (form.services_used || []).includes(s.id);
              return <button type="button" key={s.id} onClick={() => toggleService(s.id)} className={`text-xs font-bold px-3 py-1.5 rounded-full transition-all ${active ? "bg-[#06B6D4] text-[#0A1128]" : "bg-[#FAFAFA] text-[#64748B] hover:bg-black/5"}`}>{s.short}</button>;
            })}
          </div>
        </div>

        <div className="sm:col-span-2"><Label>Résumé (1-2 phrases)</Label><Textarea rows={2} value={form.summary || ""} onChange={(e) => setForm({ ...form, summary: e.target.value })} /></div>
        <div className="sm:col-span-2"><Label>Le défi</Label><Textarea rows={3} value={form.challenge || ""} onChange={(e) => setForm({ ...form, challenge: e.target.value })} /></div>
        <div className="sm:col-span-2"><Label>Notre approche</Label><Textarea rows={3} value={form.solution || ""} onChange={(e) => setForm({ ...form, solution: e.target.value })} /></div>
        <div className="sm:col-span-2"><Label>Les résultats</Label><Textarea rows={3} value={form.results || ""} onChange={(e) => setForm({ ...form, results: e.target.value })} /></div>

        <div className="sm:col-span-2">
          <Label>Métriques clés <span className="text-xs text-[#64748B]">(une par ligne, format "Valeur | Libellé")</span></Label>
          <Textarea rows={4} value={(form.metrics || []).map((m) => `${m.value} | ${m.label}`).join("\n")}
            onChange={(e) => {
              const lines = e.target.value.split("\n").map((l) => l.trim()).filter(Boolean);
              const metrics = lines.map((l) => { const [value, label] = l.split("|").map((s) => (s || "").trim()); return { value, label: label || "" }; });
              setForm({ ...form, metrics });
            }}
            placeholder="x3 | Conversion landing\n40K€ | Levés en 6 mois" />
        </div>
      </div>
    </div>
  );
}
